import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, FileText, AlertTriangle, TrendingUp } from 'lucide-react'
import Button from './ui/Button'
import { fadeInUp, staggerContainer } from '../lib/animations'
import { LIQUIMASTER_APP_URL } from '../lib/config'

interface NetworkNode {
  x: number; y: number; vx: number; vy: number; r: number; opacity: number
}

interface FloatingNumber {
  x: number; y: number; vy: number; value: string; opacity: number; size: number
}

/* Animated network + floating numbers background */
function NetworkBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const nodesRef = useRef<NetworkNode[] | null>(null)
  const numbersRef = useRef<FloatingNumber[] | null>(null)
  const isVisibleRef = useRef(true)

  const draw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    const w = canvas.clientWidth
    const h = canvas.clientHeight
    canvas.width = w * dpr
    canvas.height = h * dpr
    ctx.scale(dpr, dpr)

    // --- Nodes ---
    const nodeCount = 45
    if (!nodesRef.current) {
      nodesRef.current = Array.from({ length: nodeCount }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.5 + 0.8,
        opacity: Math.random() * 0.4 + 0.15,
      }))
    }
    const nodes = nodesRef.current

    // --- Floating numbers ---
    if (!numbersRef.current) {
      const vals = ['5.921', '2.612', '1.221', '3.847', '0.934', '7.156', '4.208', '6.573', '1.891', '9.342', '2.467', '8.115', '342.197', '1.65', '0.221']
      numbersRef.current = Array.from({ length: 15 }, (_, i) => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vy: -(Math.random() * 0.15 + 0.05),
        value: vals[i % vals.length],
        opacity: Math.random() * 0.12 + 0.04,
        size: Math.random() * 4 + 10,
      }))
    }
    const numbers = numbersRef.current

    ctx.clearRect(0, 0, w, h)

    // Move & draw numbers
    for (const n of numbers) {
      n.y += n.vy
      if (n.y < -20) { n.y = h + 20; n.x = Math.random() * w }
      ctx.font = `500 ${n.size}px "Inter", monospace`
      ctx.fillStyle = `rgba(255,255,255,${n.opacity})`
      ctx.fillText(n.value, n.x, n.y)
    }

    // Move nodes
    for (const node of nodes) {
      node.x += node.vx
      node.y += node.vy
      if (node.x < 0 || node.x > w) node.vx *= -1
      if (node.y < 0 || node.y > h) node.vy *= -1
    }

    // Draw connections
    const maxDist = 160
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const dx = nodes[i].x - nodes[j].x
        const dy = nodes[i].y - nodes[j].y
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist < maxDist) {
          const alpha = (1 - dist / maxDist) * 0.12
          ctx.beginPath()
          ctx.moveTo(nodes[i].x, nodes[i].y)
          ctx.lineTo(nodes[j].x, nodes[j].y)
          ctx.strokeStyle = `rgba(96,165,250,${alpha})`
          ctx.lineWidth = 0.6
          ctx.stroke()
        }
      }
    }

    // Draw nodes
    for (const node of nodes) {
      ctx.beginPath()
      ctx.arc(node.x, node.y, node.r, 0, Math.PI * 2)
      ctx.fillStyle = `rgba(96,165,250,${node.opacity})`
      ctx.fill()
    }
  }, [])

  useEffect(() => {
    // Skip animation for users who prefer reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) {
      draw() // Draw a single static frame
      return
    }

    let raf: number
    const loop = () => {
      if (isVisibleRef.current) {
        draw()
      }
      raf = requestAnimationFrame(loop)
    }
    loop()

    // Pause animation when off-screen
    const observer = new IntersectionObserver(
      ([entry]) => { isVisibleRef.current = entry.isIntersecting },
      { threshold: 0 }
    )
    if (containerRef.current) observer.observe(containerRef.current)

    const handleResize = () => {
      nodesRef.current = null
      numbersRef.current = null
    }
    window.addEventListener('resize', handleResize)

    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
      window.removeEventListener('resize', handleResize)
    }
  }, [draw])

  return (
    <div ref={containerRef} className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* Gradient overlays */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(30,64,120,0.45) 0%, transparent 70%)',
      }} />
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 50% 50% at 80% 20%, rgba(59,130,246,0.08) 0%, transparent 60%)',
      }} />

      {/* Canvas for network + numbers */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      {/* Subtle grid overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }} />
    </div>
  )
}

/* Financial report analysis visualization — dark theme */
function ReportAnalysisVisualization() {
  const { t } = useTranslation()
  const scanDuration = 3

  const reportRows = [
    { width: 85, isRisk: false },
    { width: 70, isRisk: false },
    { width: 92, isRisk: true },
    { width: 60, isRisk: false },
    { width: 78, isRisk: false },
    { width: 88, isRisk: true },
    { width: 55, isRisk: false },
    { width: 95, isRisk: false },
    { width: 72, isRisk: true },
    { width: 80, isRisk: false },
    { width: 65, isRisk: false },
    { width: 90, isRisk: false },
  ]

  const riskBars = [
    { label: t('hero.viz.liquidity'), value: 78, color: '#f97316' },
    { label: t('hero.viz.credit'), value: 55, color: '#fb923c' },
    { label: t('hero.viz.market'), value: 42, color: '#fdba74' },
    { label: t('hero.viz.operations'), value: 30, color: '#fed7aa' },
  ]

  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="relative w-full min-h-[480px] flex items-center justify-center"
    >
      {/* Main document card */}
      <motion.div
        className="relative w-[280px] bg-navy-800/80 rounded-2xl border border-navy-700 shadow-[0_8px_30px_rgba(0,0,0,0.3)] overflow-hidden backdrop-blur-sm"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="px-5 pt-5 pb-3 border-b border-navy-700">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-7 h-7 rounded-lg bg-orange-500/10 flex items-center justify-center">
              <FileText className="w-3.5 h-3.5 text-orange-400" />
            </div>
            <div>
              <div className="text-[11px] font-semibold text-white leading-tight">{t('hero.viz.reportTitle')}</div>
              <div className="text-[10px] text-navy-400">{t('hero.viz.reportFile')}</div>
            </div>
          </div>
          <div className="w-full h-1 bg-navy-700 rounded-full overflow-hidden mt-2">
            <motion.div
              className="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full"
              animate={{ width: ['0%', '100%'] }}
              transition={{ duration: scanDuration, repeat: Infinity, ease: 'linear', repeatDelay: 0.5 }}
            />
          </div>
          <motion.div
            className="text-[10px] text-orange-400 mt-1.5 flex items-center gap-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {t('hero.viz.analyzing')}
          </motion.div>
        </div>

        <div className="px-5 py-4 relative">
          <motion.div
            className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-orange-400 to-transparent z-10"
            animate={{ top: ['0%', '100%'], opacity: [0, 1, 1, 0] }}
            transition={{ duration: scanDuration, repeat: Infinity, ease: 'linear', times: [0, 0.05, 0.9, 1], repeatDelay: 0.5 }}
          />
          <motion.div
            className="absolute left-0 right-0 h-8 bg-gradient-to-b from-orange-500/10 to-transparent z-[5] pointer-events-none"
            animate={{ top: ['0%', '100%'], opacity: [0, 0.8, 0.8, 0] }}
            transition={{ duration: scanDuration, repeat: Infinity, ease: 'linear', times: [0, 0.05, 0.9, 1], repeatDelay: 0.5 }}
          />

          <div className="space-y-2.5">
            {reportRows.map((row, i) => {
              const rowOffset = (i / reportRows.length) * scanDuration
              return (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-8 h-2 rounded-sm bg-navy-700" />
                  <motion.div
                    className="h-2 rounded-sm"
                    style={{ width: `${row.width}%` }}
                    animate={
                      row.isRisk
                        ? { backgroundColor: ['rgba(255,255,255,0.06)', 'rgba(249,115,22,0.25)', 'rgba(249,115,22,0.12)'] }
                        : { backgroundColor: ['rgba(255,255,255,0.06)', 'rgba(255,255,255,0.1)', 'rgba(255,255,255,0.06)'] }
                    }
                    transition={{ duration: scanDuration, repeat: Infinity, delay: rowOffset, repeatDelay: 0.5, times: [0, 0.15, 1] }}
                  />
                  {row.isRisk && (
                    <motion.div
                      animate={{ scale: [0.8, 1.15, 1], opacity: [0.4, 1, 0.8] }}
                      transition={{ duration: scanDuration, repeat: Infinity, delay: rowOffset, repeatDelay: 0.5, times: [0, 0.15, 1] }}
                    >
                      <AlertTriangle className="w-3 h-3 text-orange-400 flex-shrink-0" />
                    </motion.div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </motion.div>

      {/* Floating card: Risk Score */}
      <motion.div
        className="absolute top-4 right-0 bg-navy-800/80 backdrop-blur-sm rounded-xl border border-navy-700 px-4 py-3 shadow-[0_4px_20px_rgba(0,0,0,0.3)]"
        initial={{ opacity: 0, x: 20, y: -10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 3.4, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="flex items-center gap-1.5 mb-1.5">
          <TrendingUp className="w-3 h-3 text-orange-400" />
          <span className="text-[10px] text-navy-400 font-medium tracking-wider">{t('hero.viz.riskScore')}</span>
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold text-white">73</span>
          <span className="text-[11px] text-navy-400">/100</span>
        </div>
        <div className="w-full h-1.5 bg-navy-700 rounded-full mt-2 overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-orange-400 to-orange-500"
            initial={{ width: '0%' }}
            animate={{ width: '73%' }}
            transition={{ delay: 3.6, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
      </motion.div>

      {/* Floating card: Risk Breakdown */}
      <motion.div
        className="absolute bottom-4 -right-2 bg-navy-800/80 backdrop-blur-sm rounded-xl border border-navy-700 px-4 py-3 shadow-[0_4px_20px_rgba(0,0,0,0.3)]"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.8, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="text-[10px] text-navy-400 font-medium tracking-wider mb-2.5">{t('hero.viz.riskBreakdown')}</div>
        <div className="flex items-end gap-2 h-14">
          {riskBars.map((bar, i) => (
            <div key={i} className="flex flex-col items-center gap-1">
              <motion.div
                className="w-5 rounded-sm"
                style={{ backgroundColor: bar.color }}
                initial={{ height: 0 }}
                animate={{ height: `${(bar.value / 100) * 48}px` }}
                transition={{ delay: 4.0 + i * 0.12, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              />
              <span className="text-[8px] text-navy-400">{bar.label}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Floating card: Findings */}
      <motion.div
        className="absolute top-10 -left-2 bg-navy-800/80 backdrop-blur-sm rounded-xl border border-navy-700 px-3.5 py-2.5 shadow-[0_4px_20px_rgba(0,0,0,0.3)] flex items-center gap-2.5"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 3.6, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center">
          <AlertTriangle className="w-4 h-4 text-orange-400" />
        </div>
        <div>
          <div className="text-[10px] text-navy-400 font-medium">{t('hero.viz.findings')}</div>
          <div className="flex items-baseline gap-1">
            <span className="text-base font-bold text-white">12</span>
            <motion.span
              className="text-[10px] font-medium text-orange-400"
              animate={{ opacity: [1, 0.4, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {t('hero.viz.critical')}
            </motion.span>
          </div>
        </div>
      </motion.div>

      {/* Floating card: Revenue at Risk */}
      <motion.div
        className="absolute bottom-14 -left-4 bg-navy-800/80 backdrop-blur-sm rounded-xl border border-navy-700 px-3.5 py-2.5 shadow-[0_4px_20px_rgba(0,0,0,0.3)]"
        initial={{ opacity: 0, x: -20, y: 10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 4.0, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="text-[10px] text-navy-400 font-medium">{t('hero.viz.revenueAtRisk')}</div>
        <span className="text-base font-bold text-white">{t('hero.viz.revenueValue')}</span>
        <span className="text-[10px] text-rose-400 font-medium ml-1">&darr; 12%</span>
      </motion.div>

      {/* Connecting lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ overflow: 'visible' }}>
        <motion.line
          x1="68%" y1="30%" x2="82%" y2="15%"
          stroke="rgba(249, 115, 22, 0.2)" strokeWidth="1" strokeDasharray="3 3"
          initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
          transition={{ delay: 3.5, duration: 0.5 }}
        />
        <motion.line
          x1="32%" y1="30%" x2="18%" y2="18%"
          stroke="rgba(249, 115, 22, 0.2)" strokeWidth="1" strokeDasharray="3 3"
          initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
          transition={{ delay: 3.7, duration: 0.5 }}
        />
      </svg>
    </motion.div>
  )
}

export default function Hero() {
  const { t } = useTranslation()
  const words = t('hero.titleHighlightWords', { returnObjects: true }) as string[]
  const prefix = t('hero.titleHighlightPrefix', { defaultValue: '' })
  const [wordIndex, setWordIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % words.length)
    }, 3500)
    return () => clearInterval(interval)
  }, [words.length])

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-navy-950">
      {/* Animated network + floating numbers background */}
      <NetworkBackground />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left: Text content */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="relative z-20"
          >
            <motion.div variants={fadeInUp}>
              <span className="inline-flex items-center gap-3 text-orange-400 text-sm font-medium tracking-wide mb-8">
                <span className="w-8 h-px bg-orange-400" />
                {t('hero.badge')}
              </span>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-[1.02] mb-6 tracking-tight"
            >
              <span className="block">
                {t('hero.title')}
              </span>
              <span className="block">
                {t('hero.titleLineBreak')}
              </span>
              <span className="block">
                {prefix && <>{prefix}{' '}</>}
                <span className="rotating-word-wrapper">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={words[wordIndex]}
                      initial={{ y: 24, opacity: 0, filter: 'blur(4px)' }}
                      animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
                      exit={{ y: -24, opacity: 0, filter: 'blur(4px)' }}
                      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                      className="rotating-word hero-gradient"
                    >
                      {words[wordIndex]}
                    </motion.span>
                  </AnimatePresence>
                </span>
              </span>
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="text-base sm:text-lg text-navy-300 max-w-lg mb-10 leading-relaxed"
            >
              {t('hero.subtitle')}
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row items-start gap-3"
            >
              <Button href={LIQUIMASTER_APP_URL} size="lg" variant="primary">
                {t('hero.cta')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>

            {/* Trust indicators below CTA */}
            <motion.div
              variants={fadeInUp}
              className="mt-12 flex items-center gap-6 text-sm text-navy-400"
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <span>DSGVO-konform</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <span>Deutsche Server</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Report analysis visualization */}
          <div className="hidden lg:block">
            <ReportAnalysisVisualization />
          </div>
        </div>
      </div>
    </section>
  )
}
